import { Form, Formik } from 'formik';
import { useRouter } from 'next/dist/client/router';
import React, { FC, useContext, useEffect, useState } from 'react';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import { ProductCardLabelsType } from '../../../models/product-card-labels-type';
import { ProductDetailResultType } from '../../../models/product-detail-result-type';
import { ProductDetailResultVariantType } from '../../../models/product-detail-result-variant-type';
import { ProductDetailsType } from '../../../models/product-details-type';
import ProductTypeEnum from '../../../models/product-type-enum';
import { SpecialPricingType } from '../../../models/special-pricing-type';
import pricingRequest from '../../../services/customer-api/endpoints/pricing/request';
import FormSelect from '../../atoms/form-select/form-select';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import ProductImage from '../../atoms/product-image/product-image';
import AddToBasketButton from '../../molecules/add-to-basket-button/add-to-basket-button';
import AddToFavouritesButton from '../../molecules/add-to-favourites-button/add-to-favourites-button';
import ProductAttributes from '../../molecules/product-attributes/product-attributes';
import ProductPricing from '../../molecules/product-pricing/product-pricing';
import { BasketStateContext } from '../../providers/basket/basket.provider';
import useAddToRecentBasket from '../../providers/basket/hooks/use-add-to-recent-basket';
import useIsLoggedIn from '../../providers/user/hooks/use-is-logged-in';
import UspList from '../../molecules/usp-list/usp-list';
import AnimationFadeIn from '../../animations/animation-fade-in/animation-fade-in';
import FreshRelevanceSmartBlock from '../fresh-relevance-smart-block/fresh-relevance-smart-block';
import ComponentEnum from '../../../models/component-enum';
import { ContentPageComponentDataType } from '../../../models/content-page-component-data-type';
import findMiddleImage from '../../../helpers/return-correct-property';
import NotifyMe from '../notify-me/notify-me';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
import NotifyMeModal from '../notify-me-modal/notify-me-modal';
import open from '../../providers/overlay/open.overlay.actions';
import useGetSessionId from '../../providers/user/hooks/use-get-session-id.hook';
import client from '../../../services/customer-api/client';

interface Props extends ProductDetailsType {
  readonly productDetailResult: ProductDetailResultType;
  readonly stockQuantity: number | null;
  // eslint-disable-next-line react/require-default-props
  readonly freshRelevanceSmartBlocks?: any[];
}

const ProductDetails: FC<Props> = ({
  labels,
  productDetailResult,
  stockQuantity,
  freshRelevanceSmartBlocks,
}: Props) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const [specialPrices, setSpecialPrices] = useState<SpecialPricingType[]>();
  const [variantSku, setVariantSku] = useState<string>('');
  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();
  const addToRecentBasket = useAddToRecentBasket();
  const featured =
    productDetailResult?.product.properties.featuredProduct === '1';
  const basketState = useContext(BasketStateContext);
  const [showInitial, setShowInitial] = useState<boolean>(true);

  useEffect(() => {
    if (!isLoggedIn) {
      pushToDataLayer(GtmEventNameEnum.Detail, {
        ecommerce: {
          detail: {
            products: [
              {
                price: productDetailResult.buyPriceFormatted,
                productImage:
                  productDetailResult.product.properties.webImageLarge1425,
                productName: productDetailResult.product.name,
                productCode: productDetailResult.product.variantSku,
                masterCode: productDetailResult.product.sku,
                listPrice: productDetailResult.buyPriceFormatted,
              },
            ],
          },
        },
        featuredProduct:
          productDetailResult.product.properties.featuredProduct === '1',
        productType: ProductTypeEnum.Website,
      });

      return;
    }

    pricingRequest([productDetailResult]).then(
      (response: SpecialPricingType[]) => {
        setSpecialPrices(response);

        pushToDataLayer(GtmEventNameEnum.Detail, {
          ecommerce: {
            detail: {
              products: [
                {
                  price: response[0].priceFormatted,
                  productImage:
                    productDetailResult.product.properties.webImageLarge1425,
                  productName: productDetailResult.product.name,
                  productCode: productDetailResult.product.variantSku,
                  masterCode: productDetailResult.product.sku,
                  listPrice: productDetailResult.listPriceFormatted,
                },
              ],
            },
          },
          featuredProduct:
            productDetailResult.product.properties.featuredProduct === '1',
          productType: ProductTypeEnum.Website,
        });
      }
    );
  }, [isLoggedIn, productDetailResult]);

  useEffect(() => {
    if (
      productDetailResult?.product?.variantSku &&
      productDetailResult?.product?.variantSku !== variantSku &&
      basketState
    ) {
      setVariantSku(productDetailResult.product.variantSku);
      addToRecentBasket(productDetailResult?.product.variantSku);
    }
  }, [basketState, productDetailResult?.product?.variantSku, variantSku]);

  const middleImgSource = findMiddleImage(
    productDetailResult.product.properties
  );

  const overlayDispatch = useContext(OverlayDispatchContext);
   const [sessionId, setSessionId] = useState<string | null>(null);
    const getSessionId = useGetSessionId();

    useEffect(() => {
      const fetchSessionId = async () => {
        const id = await getSessionId();
        setSessionId(id);
    };

    fetchSessionId();
  }, [getSessionId, sessionId]);

  const openNotifyMeModal = (product): void => {
  if (sessionId && typeof sessionId === "string") {
    overlayDispatch(
      open(
        <NotifyMeModal
          product={product.product}
          sessionId={sessionId}
        />,
        false,
        false,
        true,
        false,
        true
      )
    );
  }
};

  return (
    <div className="py-component-spacing-mobile lg:py-component-spacing">
      <div className="flex flex-col md:flex-row space-y-12 md:space-y-0 md:space-x-12">
        <div className="w-full md:w-6/12">
          <div
            className={
              `${featured ? 'bg-light-blue' : 'bg-grey-50'} ` +
              'rounded-lg p-16 lg:p-24 md:mb-10'
            }
          >
            {productDetailResult?.product.properties.webImageLarge1425 && (
              <ProductImage src={middleImgSource.source} />
            )}
          </div>
        </div>

        {isRedirecting && <LoadingSpinner className="h-96 w-full md:w-7/12" />}

        {!isRedirecting && (
          <div className="text-left md:text-left w-full md:w-6/12">
            {featured && (
              <span className="uppercase text-french-blue text-sm font-bold">
                Featured
              </span>
            )}

            <h1 className="page-heading-three mb-4 md:mt-4 lg:text-3xl">
              {productDetailResult.product.name}
            </h1>

            <div className="flex flex-row mb-4 md:mb-0">
              <span className="text-grey-600 text-left text-sm">
                {labels.productCodeLabel}&nbsp;
              </span>
              <span className="text-sm text-left">
                {productDetailResult.product.variantSku}
              </span>
            </div>

            {productDetailResult.product.properties.manufacturersCode &&
              productDetailResult.product.properties.manufacturersCode !==
                productDetailResult.product.variantSku && (
                <div className="flex flex-col md:flex-row">
                  <span className="text-grey-600 text-sm">
                    {labels.manufacturersReferenceLabel}&nbsp;
                  </span>
                  <span className="text-sm">
                    {productDetailResult.product.properties.manufacturersCode}
                  </span>
                </div>
              )}

            {!!productDetailResult.variantDropdowns?.length && (
              <Formik
                enableReinitialize
                initialValues={productDetailResult.variantDropdowns.reduce(
                  (acc, curr) => ({ ...acc, [curr.key]: curr.value }),
                  {}
                )}
                onSubmit={null}
              >
                <Form className="flex flex-col space-y-6 w-full md:w-10/12 lg:w-8/12 xl:w-6/12 mt-10 md:mt-6">
                  {productDetailResult.variantDropdowns?.map(
                    (variant: ProductDetailResultVariantType) => (
                      <div key={variant.key}>
                        <FormSelect
                          label={variant.name}
                          name={variant.key}
                          onChange={async (value: string) => {
                            setIsRedirecting(true);

                            await router.push(
                              variant.items.find((item) => item.value === value)
                                .url
                            );
                            setIsRedirecting(false);
                          }}
                          options={variant.items.map(({ value }) => ({
                            label: value,
                            value,
                          }))}
                          placeholder={labels.emptyVariantDropdownLabel}
                        />
                      </div>
                    )
                  )}
                </Form>
              </Formik>
            )}

            <div className="mt-8">
              <div className="w-full md:w-11/12 lg:w-9/12 xl:w-7/12">
                <ProductPricing
                  basketItem={productDetailResult}
                  labels={labels as unknown as ProductCardLabelsType}
                  specialPrice={
                    isLoggedIn && specialPrices ? specialPrices[0] : null
                  }
                />
              </div>
            </div>

            <div className="pb-2 md:pb-10 pt-3">
              <div className="flex flex-col xl:flex-row space-y-4 xl:space-y-0 xl:space-x-4 items-center xl:items-end justify-between">
                <div className="w-full md:w-10/12 lg:w-7/12 xl:w-6/12">
                  <AddToBasketButton
                    addToBasketLabel={labels.addToBasketLabel}
                    backgroundColor="light-blue"
                    addProductErrorLabels={{
                      addProductErrorHeading: labels.addProductErrorHeading,
                      addProductErrorDescription:
                        labels.addProductErrorDescription,
                      addProductErrorBody: labels.addProductErrorBody,
                      insufficientClearanceStockError:
                        labels.insufficientClearanceStockError,
                    }}
                    clearQuoteLabels={{
                      clearQuoteAccept: labels.clearQuoteAccept,
                      clearQuoteCancel: labels.clearQuoteCancel,
                      clearQuoteDescription: labels.clearQuoteDescription,
                      clearQuoteHeading: labels.clearQuoteHeading,
                      clearQuoteHelp: labels.clearQuoteHelp,
                    }}
                    paddingX="full"
                    product={productDetailResult.product}
                    onClick={() => setShowInitial(false)}
                    stockQuantity={stockQuantity}
                    alwaysShowStockAmount
                  />
                </div>

                {isLoggedIn && (
                  <div className="pb-4">
                    <AddToFavouritesButton
                      basketItem={productDetailResult}
                      labels={labels as unknown as ProductCardLabelsType}
                    />
                  </div>
                )}
              </div>
            </div>
            {labels?.uniqueSellingPointItems?.length > 0 ? (
              <UspList items={labels?.uniqueSellingPointItems} />
            ) : null}

            { stockQuantity <= 0 && (
              <NotifyMe openNotifyMeModal={() => openNotifyMeModal(productDetailResult)} product={productDetailResult} />
            )} 
          </div>
        )}
      </div>

      {freshRelevanceSmartBlocks?.map(
        (
          component: {
            name: ComponentEnum;
            data: ContentPageComponentDataType;
          },
          index
        ) => {
          return (
            <AnimationFadeIn key={component.data.id}>
              <FreshRelevanceSmartBlock
                uniqueId={component.data.properties.uniqueId}
                index={index}
                showInitial={showInitial}
              />
            </AnimationFadeIn>
          );
        }
      )}

      <div className="border-t border-grey-100 pb-4 pt-8 md:pt-12">
        <ProductAttributes {...productDetailResult} labels={labels} />
      </div>
    </div>
  );
};

export default ProductDetails;
